/** A team shows on Home after it was opened on this many different days. */
export const FREQUENT_TEAM_MIN_DAYS = 3;

/** Home shows at most this many frequent teams. */
export const FREQUENT_TEAM_LIMIT = 3;

/**
 * Qualification and rank only count this many local calendar dates, ending
 * today. A visit 30 days ago is outside the window.
 */
export const FREQUENT_TEAM_WINDOW_DAYS = 30;

const MAX_STORED_TEAMS = 30;
const MAX_STORED_DAYS = 90;

export type RecordTeamViewInput = {
  teamId: string;
  name: string;
  breadcrumb: string[];
  /** Local calendar day, YYYY-MM-DD. */
  day: string;
};

const isIsoDay = (value: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }
  const [year, month, day] = value.split("-").map(Number);
  if (
    year === undefined ||
    month === undefined ||
    day === undefined ||
    Number.isNaN(year) ||
    Number.isNaN(month) ||
    Number.isNaN(day)
  ) {
    return false;
  }
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

export const localDateIso = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const uniqueDays = (days: readonly string[]): string[] =>
  [...new Set(days.filter(isIsoDay))].sort().slice(-MAX_STORED_DAYS);

const stringList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((part): part is string => typeof part === "string")
    : [];

/** Drops junk from a preference blob so a bad write cannot break Home. */
export const readHomeTeamViews = (value: unknown): user_prefs.HomeTeamViews => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((entry) => {
    if (!entry || typeof entry !== "object") {
      return [];
    }
    const view = entry as Partial<user_prefs.HomeTeamView>;
    if (typeof view.teamId !== "string" || view.teamId.length === 0) {
      return [];
    }
    if (typeof view.name !== "string" || view.name.length === 0) {
      return [];
    }
    return [
      {
        teamId: view.teamId,
        name: view.name,
        breadcrumb: stringList(view.breadcrumb).filter(
          (part) => part.length > 0
        ),
        days: uniqueDays(stringList(view.days)),
      },
    ];
  });
};

/** Ancestor names. Hierarchy is `Parent\Child\Team`. */
export const breadcrumbFromHierarchy = (
  hierarchy: string | null | undefined,
  name: string
): string[] => {
  if (!hierarchy) {
    return [];
  }
  const parts = hierarchy
    .split("\\")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  if (parts.length === 0) {
    return [];
  }
  if (parts[parts.length - 1] === name) {
    return parts.slice(0, -1);
  }
  return parts.filter((part) => part !== name);
};

export const formatBreadcrumb = (breadcrumb: readonly string[]): string =>
  breadcrumb.join(" · ");

const sameView = (
  left: user_prefs.HomeTeamView,
  right: user_prefs.HomeTeamView
): boolean =>
  left.name === right.name &&
  left.breadcrumb.join("\u0000") === right.breadcrumb.join("\u0000") &&
  left.days.join(",") === right.days.join(",");

/** Storage order and the rank tie-break: latest day, then more stored days. */
const byRecentDay = (
  left: user_prefs.HomeTeamView,
  right: user_prefs.HomeTeamView
): number => {
  const recent = (right.days.at(-1) ?? "").localeCompare(
    left.days.at(-1) ?? ""
  );
  if (recent !== 0) {
    return recent;
  }
  return right.days.length - left.days.length;
};

const shiftLocalIsoDay = (isoDay: string, deltaDays: number): string => {
  const [year, month, day] = isoDay.split("-").map(Number);
  const date = new Date(year ?? 0, (month ?? 1) - 1, day ?? 1);
  date.setDate(date.getDate() + deltaDays);
  return localDateIso(date);
};

/** Distinct stored days on the 30 local dates ending `today`, inclusive. */
const daysWithinWindow = (days: readonly string[], today: string): string[] => {
  const start = shiftLocalIsoDay(today, 1 - FREQUENT_TEAM_WINDOW_DAYS);
  return days.filter((day) => day >= start && day <= today);
};

/** Highest 30-day count, then the recency tie-break above. */
const byRecentFrequency = (
  today: string,
  left: user_prefs.HomeTeamView,
  right: user_prefs.HomeTeamView
): number => {
  const byCount =
    daysWithinWindow(right.days, today).length -
    daysWithinWindow(left.days, today).length;
  if (byCount !== 0) {
    return byCount;
  }
  return byRecentDay(left, right);
};

/**
 * Props history plus rows this client wrote that the server has not echoed.
 * Same team id unions days. The later day wins name and breadcrumb. A tie
 * keeps the pending row so a same-day rename is not dropped.
 */
export const unionHomeTeamViews = (
  propsViews: unknown,
  pendingViews: unknown
): user_prefs.HomeTeamViews => {
  const byId = new Map<string, user_prefs.HomeTeamView>();
  for (const view of readHomeTeamViews(propsViews)) {
    byId.set(view.teamId, view);
  }
  for (const pending of readHomeTeamViews(pendingViews)) {
    const existing = byId.get(pending.teamId);
    if (!existing) {
      byId.set(pending.teamId, pending);
      continue;
    }
    const pendingDay = pending.days.at(-1) ?? "";
    const existingDay = existing.days.at(-1) ?? "";
    const pendingWins = pendingDay >= existingDay;
    byId.set(pending.teamId, {
      teamId: pending.teamId,
      name: pendingWins ? pending.name : existing.name,
      breadcrumb: pendingWins
        ? [...pending.breadcrumb]
        : [...existing.breadcrumb],
      days: uniqueDays([...existing.days, ...pending.days]),
    });
  }
  return [...byId.values()].sort(byRecentDay).slice(0, MAX_STORED_TEAMS);
};

/**
 * Adds today's visit. Returns null when the stored row is already current,
 * so the caller can skip a preference write.
 */
export const recordTeamView = (
  views: unknown,
  entry: RecordTeamViewInput
): user_prefs.HomeTeamViews | null => {
  if (
    !isIsoDay(entry.day) ||
    entry.teamId.length === 0 ||
    entry.name.length === 0
  ) {
    return null;
  }

  const current = readHomeTeamViews(views);
  const existing = current.find((view) => view.teamId === entry.teamId);
  const nextView: user_prefs.HomeTeamView = {
    teamId: entry.teamId,
    name: entry.name,
    breadcrumb: entry.breadcrumb.filter((part) => part.length > 0),
    days: uniqueDays([...(existing?.days ?? []), entry.day]),
  };

  if (existing && sameView(existing, nextView)) {
    return null;
  }

  return [nextView, ...current.filter((view) => view.teamId !== entry.teamId)]
    .sort(byRecentDay)
    .slice(0, MAX_STORED_TEAMS);
};

/**
 * Up to 3 teams opened on 3 or more separate days in the last 30 local
 * dates. Highest 30-day count first, then most recent day, then more
 * stored days. `today` is a local YYYY-MM-DD; it defaults to this device.
 */
export const selectFrequentTeams = (
  views: unknown,
  today = localDateIso(new Date())
): user_prefs.HomeTeamViews =>
  readHomeTeamViews(views)
    .filter(
      (view) =>
        daysWithinWindow(view.days, today).length >= FREQUENT_TEAM_MIN_DAYS
    )
    .sort((left, right) => byRecentFrequency(today, left, right))
    .slice(0, FREQUENT_TEAM_LIMIT);
